import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { Flex } from "styled-system/jsx";
import { Button, Card, Icon, Tag, Text } from "@ttc3k/trekker";
import { NavArrowRight, WarningTriangle } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { useEventCreateEditContext } from "../EventCreateEditContext/useEventCreateEditContext";
import { formatDate, DATE_PATTERN_STANDARD, getMultilingualString } from "shared/utils";
import {
  type AssetImage,
  useEventByIdLazyQuery,
  useOrganizationNameByIdLazyQuery,
  useOrganizationLogoByIdLazyQuery,
} from "gql/generated";
import { SmartImage } from "shared/components/atoms";

const ACTION_LABELS: Record<string, string> = {
  CREATED: "Created",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  DECLINED: "Declined",
  CANCELLED: "Cancelled",
  UNCANCELLED: "Uncancelled",
  EDITED: "Edited",
  ORG_DISCONNECTED: "Removed from org",
  ORG_ADDED: "Added to org",
};

const SOURCE_LABELS: Record<string, string> = {
  GUEST: "Guest",
  OPERATOR: "Operator",
  ORGANIZATION: "Org Admin",
};

const ORG_ACTOR_ACTIONS = new Set([
  "APPROVED",
  "DECLINED",
  "CANCELLED",
  "UNCANCELLED",
  "ORG_DISCONNECTED",
]);

/** History entries that reference an organization we should resolve for display */
const ACTIONS_REQUIRING_ORG_LOOKUP = new Set([
  ...ORG_ACTOR_ACTIONS,
  "ORG_ADDED",
]);

const ORG_ADDED_NOTE_ID_REGEX =
  /^Added via operator enrollment to org ([a-f0-9]{24})$/i;

function parseOrgIdFromOrgAddedNote(note: string | undefined): string | undefined {
  if (!note) return undefined;
  const m = note.match(ORG_ADDED_NOTE_ID_REGEX);
  return m?.[1];
}

const COLLAPSED_ENTRY_COUNT = 3;

type OrgInfo = {
  shortName?: string;
  name?: string;
  logo?: AssetImage | null;
};

export const EventHistory = () => {
  const { t } = useTranslation("events", {
    keyPrefix: "APP.MODAL.EVENT_DETAILS",
  });
  const {
    event,
    isOrganizationAdmin,
    isTnbInternalUser,
    isOperatorUser,
    mode,
    onOpenPersonDetails,
  } = useEventCreateEditContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [fetchDuplicate, { data: duplicateData }] = useEventByIdLazyQuery();
  const [duplicateFetched, setDuplicateFetched] = useState(false);

  const hasSubmitterInfo =
    event?.createdByEmail ||
    event?.createdByFirstName ||
    event?.createdByLastName;
  const showCard =
    (isOrganizationAdmin || isTnbInternalUser || isOperatorUser) &&
    mode === "edit" &&
    event &&
    hasSubmitterInfo;

  const history = [...(event?.history ?? [])]
    .filter((entry): entry is NonNullable<typeof entry> => entry != null)
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );

  const suspectedDuplicateId = (event as { suspectedDuplicateOf?: string | null } | undefined)
    ?.suspectedDuplicateOf;

  if (suspectedDuplicateId && !duplicateFetched) {
    setDuplicateFetched(true);
    fetchDuplicate({ variables: { id: suspectedDuplicateId } });
  }

  const duplicateEvent = duplicateData?.eventById;

  const submitterPersonId =
    (event as { submitterPersonId?: string | null } | undefined)
      ?.submitterPersonId ?? event?.createdByPerson;
  const canOpenPerson = !!submitterPersonId && !!onOpenPersonDetails;

  // Fetch org info (shortName + logo) for status-change history entries
  const [orgInfoMap, setOrgInfoMap] = useState<Record<string, OrgInfo>>({});
  const [fetchOrgName] = useOrganizationNameByIdLazyQuery();
  const [fetchOrgLogo] = useOrganizationLogoByIdLazyQuery();

  const orgIdsToFetch = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of event?.history ?? []) {
      if (!entry) continue;
      if (entry.organizationId && ACTIONS_REQUIRING_ORG_LOOKUP.has(entry.action)) {
        ids.add(entry.organizationId);
      } else if (entry.action === "ORG_ADDED" && entry.note) {
        const parsed = parseOrgIdFromOrgAddedNote(entry.note);
        if (parsed) ids.add(parsed);
      }
    }
    return Array.from(ids);
  }, [event?.history]);

  useEffect(() => {
    for (const orgId of orgIdsToFetch) {
      if (orgInfoMap[orgId]) continue;
      fetchOrgName({ variables: { id: orgId } }).then(({ data }) => {
        const org = data?.organizationById;
        setOrgInfoMap(prev => ({
          ...prev,
          [orgId]: {
            shortName: org?.shortName ?? undefined,
            name: org?.name ? getMultilingualString(org.name) : undefined,
          },
        }));
      });
      fetchOrgLogo({ variables: { id: orgId } }).then(({ data }) => {
        const logo = data?.organizationById?.logo;
        setOrgInfoMap(prev => ({
          ...prev,
          [orgId]: { ...prev[orgId], logo: logo as AssetImage | null },
        }));
      });
    }
  }, [orgIdsToFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!showCard) return null;

  if (history.length === 0) {
    const submitterName =
      [event?.createdByFirstName, event?.createdByLastName]
        .filter(Boolean)
        .join(" ") || event?.createdByEmail;

    return (
      <Card.Surface
        cursor={canOpenPerson ? "pointer" : undefined}
        onClick={
          canOpenPerson
            ? (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenPersonDetails!(submitterPersonId!);
              }
            : undefined
        }
        _hover={canOpenPerson ? { bgColor: "bg.subtle" } : undefined}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          py="300"
          px="400"
          gap="300"
        >
          <Flex flexDir="column" gap="75">
            <Text visual="smallSemiBold" color="text.mid">
              {t("SUBMITTED_BY.TITLE")}
            </Text>
            <Text visual="bodySemiBold" color="text.dark">
              {submitterName || event?.createdByEmail}
            </Text>
            {event?.createdDateISO && (
              <Text visual="smallRegular" color="text.light">
                {formatDate(event.createdDateISO, DATE_PATTERN_STANDARD)}
              </Text>
            )}
          </Flex>
          {canOpenPerson && (
            <Button type="button" visual="unstyled" size="sm">
              <Flex alignItems="center" gap="75">
                {t("SUBMITTED_BY.VIEW_PERSON")}
                <Icon Element={NavArrowRight} />
              </Flex>
            </Button>
          )}
        </Flex>
      </Card.Surface>
    );
  }

  const visibleEntries = isExpanded
    ? history
    : history.slice(0, COLLAPSED_ENTRY_COUNT);
  const hasMore = history.length > COLLAPSED_ENTRY_COUNT;

  const createdEntry = history[history.length - 1];
  const isCreatedEntry = (entry: (typeof history)[0]) =>
    entry === createdEntry && entry.action === "CREATED";

  const renderHistoryNote = (entry: (typeof history)[0]) => {
    if (entry.action === "ORG_ADDED") {
      const orgId =
        entry.organizationId ?? parseOrgIdFromOrgAddedNote(entry.note ?? "");
      if (orgId) {
        if (orgInfoMap[orgId] === undefined) {
          return (
            <Text visual="smallRegular" color="text.light">
              {t("EVENT_HISTORY.ORG_ADDED_NOTE_LOADING")}
            </Text>
          );
        }
        const displayName =
          orgInfoMap[orgId]?.shortName || orgInfoMap[orgId]?.name;
        const nameToShow =
          displayName ?? t("EVENT_HISTORY.ORG_ADDED_UNKNOWN_ORG");
        return (
          <Text visual="smallRegular" color="text.light">
            {t("EVENT_HISTORY.ORG_ADDED_NOTE_LEADING")}
            <Text as="span" visual="smallSemiBold">
              {nameToShow}
            </Text>
          </Text>
        );
      }
    }
    if (!entry.note) return null;
    return (
      <Text visual="smallRegular" color="text.light">
        {entry.note}
      </Text>
    );
  };

  return (
    <Card.Surface>
      <Flex flexDir="column" py="300" px="400" gap="200">
        <Text visual="smallSemiBold" color="text.mid">
          {t("EVENT_HISTORY.TITLE")}
        </Text>

        {suspectedDuplicateId && duplicateEvent && (
          <Flex
            alignItems="flex-start"
            gap="100"
            p="150"
            borderRadius="100"
            bgColor="bg.warning"
          >
            <Icon Element={WarningTriangle} size="sm" color="text.warning" />
            <Flex flexDir="column" gap="25">
              <Text visual="smallSemiBold">
                {t("EVENT_HISTORY.POSSIBLE_DUPLICATE")}
              </Text>
              <Text visual="smallRegular">
                &ldquo;{duplicateEvent.title
                  ? Object.values(duplicateEvent.title).find(v => typeof v === "string" && v)
                  : ""}&rdquo;
                {duplicateEvent.startDate && (
                  <> on {formatDate(duplicateEvent.startDate, DATE_PATTERN_STANDARD)}</>
                )}
              </Text>
            </Flex>
          </Flex>
        )}

        {visibleEntries.map((entry, idx) => (
          <Flex
            key={`${entry.action}-${entry.timestamp}-${idx}`}
            alignItems="flex-start"
            justifyContent="space-between"
            gap="200"
          >
            <Flex flexDir="column" gap="25">
              <Flex alignItems="center" gap="100" flexWrap="wrap">
                <Text visual="smallSemiBold" color="text.dark">
                  {ACTION_LABELS[entry.action] ?? entry.action}
                </Text>
                {entry.organizationId &&
                ORG_ACTOR_ACTIONS.has(entry.action) &&
                orgInfoMap[entry.organizationId] ? (
                  <Flex alignItems="center" gap="75">
                    {orgInfoMap[entry.organizationId].logo && (
                      <SmartImage
                        image={orgInfoMap[entry.organizationId].logo!}
                        width={18}
                        height={18}
                        shape="circle"
                      />
                    )}
                    <Text visual="smallRegular" color="text.mid">
                      by{" "}
                      {orgInfoMap[entry.organizationId].shortName ||
                        orgInfoMap[entry.organizationId].name}
                    </Text>
                  </Flex>
                ) : (entry.userName || entry.userEmail) ? (
                  isCreatedEntry(entry) && canOpenPerson ? (
                    <Text
                      visual="smallRegular"
                      color="text.action"
                      cursor="pointer"
                      _hover={{ textDecoration: "underline" }}
                      onClick={(e: MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenPersonDetails!(submitterPersonId!);
                      }}
                    >
                      by {entry.userName || entry.userEmail}
                    </Text>
                  ) : (
                    <Text visual="smallRegular" color="text.mid">
                      by {entry.userName || entry.userEmail}
                    </Text>
                  )
                ) : null}
                {entry.sourceType && SOURCE_LABELS[entry.sourceType] && (
                  <Tag visual="primary" mode="light" size="sm">
                    {SOURCE_LABELS[entry.sourceType]}
                  </Tag>
                )}
              </Flex>
              {renderHistoryNote(entry)}
            </Flex>
            <Text
              visual="smallRegular"
              color="text.light"
              flexShrink={0}
            >
              {formatDate(entry.timestamp, DATE_PATTERN_STANDARD)}
            </Text>
          </Flex>
        ))}

        {hasMore && (
          <Button
            type="button"
            visual="unstyled"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Text visual="smallRegular" color="text.action">
              {isExpanded
                ? t("EVENT_HISTORY.SHOW_LESS")
                : t("EVENT_HISTORY.SEE_FULL_HISTORY")}
            </Text>
          </Button>
        )}
      </Flex>
    </Card.Surface>
  );
};
